/**
 * =========================================================================================
 *     Copyright © 2025 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   warehouseDetailHelp.ts
 *     Description     :   Help content data for the Warehouse Detail Page
 *     Author          :   Peter Dobler
 *     Created         :   2026-02-16
 *     Modified        :   2026-02-16 | Peter Dobler | Initial implementation
 *     Constitution    :   v1.12.0 (Enterprise Dependency Architecture)
 *     Feature         :   135-ui-data-alignment
 * =========================================================================================
 *
 *  Structured help content consumed by <PageHelpPanel> on the Warehouse
 *  Detail Page. Covers tabs, Fabric IDs, warehouse types, schema scanning,
 *  and storage mapping.
 *
 *  @module modules/backup/help
 *  @spec 135-ui-data-alignment
 *  @story US6 - Self-Service Help
 */

import type { PageHelpContent } from '../../../components/ui/PageHelpPanel';

// =============================================================================
// TOOLTIP STRINGS
// =============================================================================

export const warehouseFieldTooltips = {
  fabricId:
    'Unique identifier assigned by Microsoft Fabric. Used for API calls, logs, and cross-referencing with Fabric admin portal.',
  warehouseType:
    'The Fabric item type of this warehouse, as Fabric reports it (for example Warehouse).',
  status:
    'Active = currently accessible in Fabric. Inactive = paused or deleted in the tenant.',
  lastChangedAt:
    'When collection last recorded a change to this warehouse. A warehouse that has not changed keeps the date of its last change.',
  fabricLastUpdatedAt:
    'When Microsoft Fabric last updated this warehouse, as Fabric reports it. "Not collected" means Fabric has not reported a time for it yet.',
  capacityType:
    'Fabric capacity SKU type (F, P, EM, A) hosting this warehouse. Affects performance and billing.',
  tabs: {
    overview: 'Basic warehouse information, workspace, capacity, and status details.',
    storage: 'Azure Storage Account mappings for warehouse backup destinations.',
    config: 'Warehouse metadata and configuration settings.',
  },
} as const;

// =============================================================================
// HELP PANEL CONTENT
// =============================================================================

export const warehouseDetailHelp: PageHelpContent = {
  pageTitle: 'Warehouse Detail',
  purpose:
    'View and manage a single Fabric Warehouse — status, storage mappings, and metadata configuration.',
  sections: [
    {
      title: 'Key Concepts',
      concepts: [
        {
          term: 'Fabric ID',
          definition:
            'Globally unique identifier assigned by Microsoft Fabric. Appears in Fabric admin portal and API responses.',
        },
        {
          term: 'Warehouse Type',
          definition:
            'The Fabric item type, as Fabric reports it (for example Warehouse). A warehouse with no type recorded shows a dash.',
        },
        {
          term: 'Active / Inactive',
          definition:
            'Active = warehouse is accessible in the Fabric tenant. Inactive = deleted, paused, or no longer visible to the control plane.',
        },
        {
          term: 'Discovered vs Last Seen',
          definition:
            'Discovered = first time the collection job found this warehouse. Last Seen = most recent confirmation it still exists.',
        },
        {
          term: 'Storage Mapping',
          definition:
            'Links this warehouse to an Azure Storage Account for backup file storage.',
        },
      ],
    },
    {
      title: 'Tab Guide',
      paragraphs: [
        'Overview — Core warehouse info: name, Fabric ID, type, status, workspace, capacity details, and timestamps.',
        'Storage Accounts — Add or remove Azure Storage Account mappings for backup destinations.',
        'Configuration — Warehouse metadata settings and operational parameters.',
      ],
    },
    {
      title: 'How To',
      steps: [
        { step: 1, instruction: 'Check the onboarding banner at the top for next steps.' },
        { step: 2, instruction: 'Review the status chips to confirm the warehouse is Active.' },
        { step: 3, instruction: 'Go to Storage tab to configure backup storage if needed.' },
        { step: 4, instruction: 'Check timestamps to verify the warehouse is still being collected.' },
      ],
    },
    {
      title: 'Related Pages',
      links: [
        { label: 'Warehouses List', href: '/warehouses' },
        { label: 'Workflow Dashboard', href: '/workflows' },
        { label: 'Dashboard', href: '/dashboard' },
      ],
      defaultCollapsed: true,
    },
  ],
};
